import { getEventRules, hasEventPrizes } from './eventPermissions';

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const formatDate = (value) => {
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  return year && month && day ? `${day}-${month}-${year}` : String(value);
};

const medal = (place) => place === 1 ? '🥇' : place === 2 ? '🥈' : place === 3 ? '🥉' : '🎖️';

const stripHtml = (value) => {
  if (typeof document !== 'undefined') {
    const container = document.createElement('div');
    container.innerHTML = String(value || '');
    return (container.textContent || '').replace(/\s+/g, ' ').trim();
  }
  return String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

const getPostPreviewImage = (post) => {
  const directImage = post?.coverImage || (Array.isArray(post?.images) ? post.images.find(Boolean) : null);
  if (directImage) return directImage;
  const match = String(post?.content || '').match(/<img[^>]+src=["']([^"']+)["']/i);
  return match?.[1] || '';
};

export const extractLinkedPostIds = (content) => {
  const ids = new Set();
  const tokenPattern = /\$منشور\s*(?::\s*([A-Za-z0-9_-]+)|\[([A-Za-z0-9_-]+)\])/g;
  let match;
  while ((match = tokenPattern.exec(String(content || ''))) !== null) {
    ids.add(match[1] || match[2]);
  }
  return [...ids];
};

export const extractWinnerUserIds = (content) => {
  const ids = new Set();
  const tokenPattern = /\$فائز\s*(?::\s*([A-Za-z0-9_-]+)|\[([A-Za-z0-9_-]+)\])/g;
  let match;
  while ((match = tokenPattern.exec(String(content || ''))) !== null) {
    ids.add(match[1] || match[2]);
  }
  return [...ids];
};

const renderLinkedPost = (postId, linkedPosts) => {
  const linkedPost = linkedPosts?.[postId] || null;
  const title = linkedPost?.title || 'منشور مرتبط';
  const excerpt = stripHtml(linkedPost?.content || 'اضغط لعرض المنشور كاملاً').slice(0, 115);
  const image = getPostPreviewImage(linkedPost);
  return `<span class="embedded-post-card" data-post-id="${escapeHtml(postId)}" role="button" tabindex="0" dir="rtl">${image ? `<img src="${escapeHtml(image)}" alt="" />` : '<span class="embedded-post-card__placeholder">📝</span>'}<span class="embedded-post-card__copy"><strong>${escapeHtml(title)}</strong><span>${escapeHtml(excerpt || 'اضغط لعرض المنشور كاملاً')}</span></span></span>`;
};

// Plain ranking text by design. This is emitted without hashtag markup or a
// data-hashtag attribute, so it remains a visual place label only.
const winnerRankLabel = (rank) => `#${rank}`;

const renderWinner = (userId, rank, linkedUsers) => {
  const user = linkedUsers?.[userId] || null;
  const name = user?.name || 'فائز';
  const tag = user?.tag || '';
  const image = user?.sensitive_img
    ? '/assets/images/global/user_sensitive_img_v2.jpg'
    : (user?.img || '/assets/images/chat/user_profile.png');
  const frame = user?.frame_url || '';

  return `<span class="embedded-winner-card embedded-winner-card--rank-${Math.min(rank, 4)}" data-user-id="${escapeHtml(userId)}" role="button" tabindex="0" dir="rtl"><span class="embedded-winner-card__rank">${winnerRankLabel(rank)}</span><span class="embedded-winner-card__avatar${frame ? ' has-frame' : ''}">${frame ? `<img class="embedded-winner-card__frame" src="${escapeHtml(frame)}" alt="" />` : ''}<img class="embedded-winner-card__image" src="${escapeHtml(image)}" alt="" /></span><span class="embedded-winner-card__copy"><strong>${escapeHtml(name)}</strong>${tag ? `<span dir="ltr">${escapeHtml(tag)}</span>` : ''}</span></span>`;
};

const renderDates = (post) => {
  if (!post?.startDate && !post?.endDate) return '';
  const start = formatDate(post.startDate);
  const end = formatDate(post.endDate);
  const range = start && end ? `${start} — ${end}` : start || end;
  return `<span class="event-inline-meta event-inline-meta--dates" dir="rtl"><span class="event-inline-meta__title">📅 موعد الفعالية</span><span class="event-inline-meta__date-value" dir="ltr">${escapeHtml(range)}</span></span>`;
};

const renderRules = (post) => {
  const rules = getEventRules(post);
  if (!rules.length) return '';
  const rows = rules.map((rule, index) => `<span class="event-inline-meta__row"><b>${index + 1}</b><span>${escapeHtml(rule)}</span></span>`).join('');
  return `<span class="event-inline-meta event-inline-meta--rules" dir="rtl"><span class="event-inline-meta__title">📜 القوانين الرسمية</span><span class="event-inline-meta__list">${rows}</span></span>`;
};

const renderPrizes = (post) => {
  if (!hasEventPrizes(post)) return '';
  if (post.allWinners) {
    return `<span class="event-inline-meta event-inline-meta--prizes" dir="rtl"><span class="event-inline-meta__title">✨ الجوائز</span><span class="event-inline-meta__winner"><strong>جميع المشاركين فائزون</strong><b><img src="/assets/images/global/coin.png" alt="" />${Number(post.coinsForAll || 0).toLocaleString('ar')}</b></span></span>`;
  }
  const prizes = post.prizes.map((prize) => `<span class="event-inline-meta__prize"><span>${medal(Number(prize.place))}</span><strong>المركز ${escapeHtml(prize.place)}</strong><b><img src="/assets/images/global/coin.png" alt="" />${Number(prize.coins || 0).toLocaleString('ar')}</b></span>`).join('');
  return `<span class="event-inline-meta event-inline-meta--prizes" dir="rtl"><span class="event-inline-meta__title">✨ الجوائز</span><span class="event-inline-meta__prize-grid">${prizes}</span></span>`;
};

const decodeCountdown = (value) => {
  try {
    const base64 = String(value).replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(escape(atob(base64))));
  } catch (_) { return null; }
};

export const getPostCountdowns = (content) => Array.from(
  String(content || '').matchAll(/\$تنازلي\[([A-Za-z0-9_-]+)\]/g),
  (match) => { const data = decodeCountdown(match[1]); return { end: new Date(data?.endsAt || '').getTime(), action: String(data?.action || 'NONE').toUpperCase() }; },
).filter(value => Number.isFinite(value.end));
export const getCountdownEndTimes = (content) => getPostCountdowns(content).map(value => value.end);

const renderCountdown = (encoded, language) => {
  const data = decodeCountdown(encoded);
  const end = data?.endsAt ? new Date(data.endsAt) : null;
  if (!end || Number.isNaN(end.getTime())) return `$تنازلي[${escapeHtml(encoded)}]`;
  const remaining = Math.max(0, Math.ceil((end.getTime() - Date.now()) / 1000) * 1000);
  const days = Math.floor(remaining / 86400000);
  const hours = Math.floor((remaining % 86400000) / 3600000);
  const minutes = Math.floor((remaining % 3600000) / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  const ar = language !== 'en';
  const units = [[days, ar ? 'يوم' : 'Days'], [hours, ar ? 'ساعة' : 'Hours'], [minutes, ar ? 'دقيقة' : 'Min'], [seconds, ar ? 'ثانية' : 'Sec']];
  const digits = units.filter((_, index) => days || index > 0).map(([value, label]) => `<span class="post-countdown-unit"><strong>${String(value).padStart(2, '0')}</strong><small>${label}</small></span>`).join('');
  const icon = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="${remaining ? 'M12 7v5l3 2' : 'm8 12 3 3 5-6'}"/></svg>`;
  return `<span class="post-countdown-card${remaining ? '' : ' post-countdown-card--ended'}" dir="${ar ? 'rtl' : 'ltr'}"><span class="post-countdown-heading">${icon}<span>${remaining ? (ar ? 'الوقت المتبقي' : 'Time remaining') : (ar ? 'انتهى العد التنازلي' : 'Countdown ended')}</span></span>${remaining ? `<span class="post-countdown-units" dir="ltr">${digits}</span>` : ''}</span>`;
};

const normalizeEventTokenLayout = (html) => {
  if (typeof document === 'undefined') return html;
  const container = document.createElement('div');
  container.innerHTML = html;

  const isEmptyTokenArtifact = (node) => {
    if (!node) return false;
    if (node.nodeType === Node.TEXT_NODE) return !node.textContent.trim();
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    if (node.tagName === 'BR') return true;
    return node.tagName === 'SPAN'
      && !node.textContent.trim()
      && Array.from(node.childNodes).every(isEmptyTokenArtifact);
  };

  container.querySelectorAll('.event-inline-meta').forEach((card) => {
    // Tiptap can retain the inline span that originally wrapped the raw token.
    // Unwrap the token's inline container without discarding authored breaks.
    // A block card already aligns to the next line; extra <br>s are meaningful.
    let wrapper = card.parentElement;
    while (
      wrapper
      && wrapper !== container
      && wrapper.tagName === 'SPAN'
      && Array.from(wrapper.childNodes).every((node) => node === card || isEmptyTokenArtifact(node))
    ) {
      wrapper.replaceWith(...wrapper.childNodes);
      wrapper = card.parentElement;
    }
  });

  return container.innerHTML;
};

export const injectEventMetadata = (content, post, linkedPosts = {}, linkedUsers = {}, language = 'ar') => {
  const html = String(content || '');
  if (!html.includes('$')) return html;

  let rendered = html.replace(
    /\$منشور\s*(?::\s*([A-Za-z0-9_-]+)|\[([A-Za-z0-9_-]+)\])/g,
    (_match, colonId, bracketId) => renderLinkedPost(colonId || bracketId, linkedPosts)
  );

  let winnerRank = 0;
  rendered = rendered.replace(
    /\$فائز\s*(?::\s*([A-Za-z0-9_-]+)|\[([A-Za-z0-9_-]+)\])/g,
    (_match, colonId, bracketId) => {
      winnerRank += 1;
      return renderWinner(colonId || bracketId, winnerRank, linkedUsers);
    }
  );

  rendered = rendered.replace(/\$تنازلي\[([A-Za-z0-9_-]+)\]/g, (_match, encoded) => renderCountdown(encoded, language));

  if (post?.event === true) {
    rendered = rendered
      .replace(/\$التاريخ/g, () => renderDates(post))
      .replace(/\$القوانين/g, () => renderRules(post))
      .replace(/\$(?:الجوائز|الجائزة)/g, () => renderPrizes(post));
  }

  return normalizeEventTokenLayout(rendered);
};
